import { getTheme, IButtonStyles, IContextualMenuItem, IContextualMenuProps } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPDefaultButton, IPSpinner, IPText, IPTooltipHost } from 'baseComponents';
import { IPContextualMenuItemType } from 'baseComponents/ContextualMenu/IPContextualMenu';
import { IPDirectionalHint } from 'baseComponents/Dialog/IPDialog';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { AIIcon } from 'components/Copilot/AIIcon';
import { getAIAssistantName } from 'components/Copilot/aiAssistantName';
import { isCopilotPanelAvailable } from 'components/Copilot/copilotPanelAvailability';
import { TaskAssistantAction } from 'components/Copilot/TaskAssistant/taskAssistant';
import ModifyTextDialog from 'components/Dialogs/ModifyTextDialog';
import FormUpgradeModal from 'components/Tasks/Forms/FormUpgradeModal';
import { getSearchableMenuProps } from 'components/Utils/SearchableMenuList';
import {
  deleteIcon,
  editIcon,
  eventIcon,
  formIcon,
  globalFontBoldWeight,
  globalStackTokensGapSmall,
  moreIcon,
  newIcon,
  settingsIcon,
  taskIcon,
  templateIcon,
  uploadIcon,
} from 'globalStyles';
import { PermissionTypes } from 'models/auth/rolePermission';
import { isAuditCriteriaPreviewEnabled } from 'models/copilot/copilotPreviewFeatureToggles';
import EvidenceLocation from 'models/evidenceLocation';
import ResourceList from 'models/resourceList';
import Task, { TaskStateId, TaskTypes } from 'models/tasks/task';
import TaskType, { SystemTaskTypes, TaskKinds } from 'models/tasks/taskType';
import { TaskType_Translation } from 'models/tasks/taskType_Translation';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiAddTaskType, apiGetTaskEvidenceLocation } from 'services/Api/taskService';
import { apiRequest } from 'services/Auth/authConfig';
import {
  FeatureTypes,
  hasUserFeature,
  hasUserFeatureGenericManager,
  hasUserRolePermission,
} from 'services/Auth/featurePermissions';

import { useHistory } from 'react-router-dom';
import AppError from 'utils/appError';
import { sortOnString } from 'utils/sorting';
import { canUpdateTaskField, TaskFieldTypes } from './TaskAuthHelper';

interface ITaskTopTasksBarProps {
  orgTask: Task;
  task: Task;
  templateTypes?: TaskTypes[];
  systemTaskType?: SystemTaskTypes;
  navigateExternal?: boolean;
  compact?: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  canSave: boolean;
  isLoading: boolean;
  isActionPending: boolean;
  onClickFinish?: () => void;
  onClickArchive?: () => void;
  onClickReopen?: () => void;
  onClickForm?: (taskTypeId: number) => void;
  onClickFollowUp?: () => void;
  onClickTemplate?: (template: Task) => void;
  onClickOpenMaster?: (taskId: number | undefined) => void;
  onClickRemove?: (taskId: number) => void;
  onCreateNewTaskTemplate?: (taskId: number) => void;
  onSaveAsNewTask?: (taskId: number) => void;
  onClickOpenEvent?: (taskId: number | undefined) => void;
  onRunTemplateWorkflows?: (task: Task) => void;
  onConvertChecklist?: () => void;
  onConvertTaskType?: (targetType: TaskTypes) => void;
  //the open instances of this series master moved to the form's current version on the server
  onInstancesFormUpgraded?: () => void | Promise<void>;
  //the category and the task's folder in it, resolved on the click
  onUploadEvidence?: (list: ResourceList, location: EvidenceLocation) => void;
  //whether the task's form has a field to add a document; undefined while the form is not loaded.
  //Set by the normal, event and monitoring task; other hosts keep the button hidden on a task with a form
  formOffersDocumentUpload?: boolean;
  //the AI assistant beside the task. The button shows only when the host passes onOpenAssistant: the
  //task types that host the pane (normal, event, monitoring) do, audit, approval and template tasks
  //do not. A click on the button while the pane is open closes it, like the AI button on the
  //navigation bar; otherwise it opens the menu of assistant actions.
  assistantAction?: TaskAssistantAction;
  onOpenAssistant?: (action: TaskAssistantAction | undefined) => void;
  //the evidence settings of a template, for managers; passed by the template and event template editors
  onEvidenceLocation?: () => void;
}

const evidenceLocationKey = 'evidenceLocation';

const TaskTopTasksBar = (props: ITaskTopTasksBarProps) => {
  const { t } = useTranslation(['translation', 'task', 'copilot', 'exceptions']);
  const appContext = useContext(AppContext);
  const history = useHistory();
  const [templates, setTemplates] = useState<Task[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState<boolean>(false);
  const [isManager] = useState<boolean>(hasUserFeatureGenericManager(appContext));
  const [hasEvidenceCategory, setHasEvidenceCategory] = useState<boolean>(false);
  const [isResolvingEvidence, setIsResolvingEvidence] = useState<boolean>(false);
  const [showCreateNewFormDialog, setShowCreateNewFormDialog] = useState<boolean>(false);
  const [newTaskTypeName, setNewTaskTypeName] = useState<string | undefined>(undefined);
  //the series master whose open instances the upgrade modal offers to move to the form's current version
  const [upgradeTarget, setUpgradeTarget] = useState<{ taskTypeFamilyId: number; taskMasterId: number } | undefined>(
    undefined,
  );
  const theme = getTheme();

  const topTaskButtonRoot = {
    color: appContext.useDarkMode ? theme.palette.themeLight : theme.palette.themeSecondary,
    height: props.compact || appContext.isMobileView ? 32 : 40,
    borderRadius: 6,
    borderColor: theme.palette.themeSecondary,
  };
  const topTaskButtonActiveBackground = appContext.useDarkMode
    ? theme.palette.themeDarkAlt
    : theme.palette.themeLighterAlt;

  const topTaskButtonStyles: IButtonStyles = {
    root: topTaskButtonRoot,
    rootHovered: {
      background: topTaskButtonActiveBackground,
    },
    rootExpanded: {
      background: topTaskButtonActiveBackground,
    },
    rootPressed: {
      background: topTaskButtonActiveBackground,
    },
  };

  //the more button carries an icon only: half the default button width
  const moreButtonStyles: IButtonStyles = {
    ...topTaskButtonStyles,
    root: { ...topTaskButtonRoot, minWidth: 40, width: 40, paddingLeft: 0, paddingRight: 0 },
  };

  //the AI button while its pane is open: shown pressed, so the state of the pane is readable from the bar
  const assistantOpenButtonStyles: IButtonStyles = {
    ...topTaskButtonStyles,
    root: { ...topTaskButtonRoot, background: topTaskButtonActiveBackground },
  };

  const navigateToTemplates = () => {
    const url = '/admin/content/templates';
    history.push(url);
  };

  const getTemplates: IContextualMenuProps = useMemo(() => {
    const output: IContextualMenuProps = {
      items: [],
      directionalHint: IPDirectionalHint.bottomLeftEdge,
      directionalHintFixed: true,
      ...getSearchableMenuProps({ pinnedKeys: ['manage-templates'] }),
    };

    let templatesToShow = [...templates];

    //When specified, filter templates to only show those of the specified types
    if (props.templateTypes && props.templateTypes.length > 0) {
      templatesToShow = templatesToShow.filter((t) => props.templateTypes?.some((tt) => tt === t.taskType));
    }

    if (isManager && !props.navigateExternal) {
      output.items.push({
        key: 'manage-templates',
        iconProps: editIcon,
        text: t('task:TopTasks.NoTemplateItems'),
        onClick: () => navigateToTemplates(),
      });
      output.items.push({
        key: 'h1',
        itemType: IPContextualMenuItemType.Divider,
        text: '-',
      });
    }

    if (templatesToShow && templatesToShow.length > 0) {
      output.items.push(
        ...templatesToShow.map((_itm: Task) => {
          return {
            key: `${_itm.taskId}`,
            text: `${_itm.name}`,
            iconProps: _itm.taskType === TaskTypes.Template ? taskIcon : eventIcon,
            canCheck: true,
            checked: props.task.templateId === _itm.taskId,
            onClick: () => {
              if (props.onClickTemplate) props.onClickTemplate(_itm);
            },
          };
        }),
      );
    } else {
      output.items.push({
        key: 'no-items',
        onRenderContent: () => {
          if (templatesLoading) {
            return (
              <IPStack horizontal tokens={globalStackTokensGapSmall}>
                <IPSpinner />
                <IPText>{t('task:TopTasks.TemplatesLoading')}</IPText>
              </IPStack>
            );
          } else {
            return <IPText styles={{ root: { padding: '0 4px' } }}>{t('task:TopTasks.NoTemplateItemsUser')}</IPText>;
          }
        },
      });
    }

    return output;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates, templatesLoading, props.templateTypes]);

  const renderFollowUpButton = () => {
    return (
      <IPStack.Item>
        <IPDefaultButton
          className="redlab-usetiful-task-topbar-followup"
          styles={topTaskButtonStyles}
          iconProps={{ iconName: 'SetAction' }}
          text={t('task:TopTasks.FollowUp')}
          disabled={props.isActionPending}
          onClick={() => props.onClickFollowUp?.()}
        />
      </IPStack.Item>
    );
  };

  //
  // Upload evidence
  //
  useEffect(() => {
    if (!props.onUploadEvidence || !props.task.isWorkableTask()) return;
    loadHasEvidenceCategory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.task.taskId, props.task.templateId]);

  const loadHasEvidenceCategory = async () => {
    try {
      setHasEvidenceCategory(false);
      const bindings = appContext.globalDataCache.outputLocationBindings;
      await bindings.getItems();
      if (bindings.hasCategoryFor([props.task.templateId])) {
        setHasEvidenceCategory(true);

        return;
      }

      //an instance of a series and a sub-task of an event can take the template of their series or event, which
      //the task does not carry: the server answers for them, without creating the folder
      if (props.task.taskId > 0 && props.task.isInstance()) {
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const location = await apiGetTaskEvidenceLocation(props.task.taskId, false, accessToken);
        setHasEvidenceCategory(location.bound);
      }
    } catch (err) {
      appContext.setError(err);
    }
  };

  //the category from the lists cache, read again once when it is missing: a category made for a template
  //after the cache was loaded
  const getEvidenceList = async (listId: number | undefined): Promise<ResourceList | undefined> => {
    if (listId === undefined) return undefined;
    const lists = appContext.globalDataCache.lists;
    if (lists.get(listId).listId !== listId) {
      await lists.getItems(true);
    }
    const list = lists.get(listId);

    return list.listId === listId ? list : undefined;
  };

  const uploadEvidence = async () => {
    if (!props.onUploadEvidence) return;
    try {
      setIsResolvingEvidence(true);
      let location: EvidenceLocation;
      if (props.task.taskId > 0) {
        //ensure finds or creates the task's folder, so the upload starts in it
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        location = await apiGetTaskEvidenceLocation(props.task.taskId, true, accessToken);
      } else {
        //an unsaved task has no folder of its own: its evidence goes into the start folder of the category
        const effective = appContext.globalDataCache.outputLocationBindings.getEffective([props.task.templateId]);
        location = new EvidenceLocation();
        location.listId = effective?.listId;
        location.folderDriveItemId = effective?.folderDriveItemId;
        location.folderName = effective?.folderName;
        location.bound = location.listId !== undefined;
      }

      const list = location.bound ? await getEvidenceList(location.listId) : undefined;
      if (!list) {
        appContext.showNotification(t('exceptions:OutputLocation.NotBound'), true, false, true);

        return;
      }

      props.onUploadEvidence(list, location);
    } catch (err) {
      //shown bottom-center: the bar sits in the task modal
      const msg = AppError.getControllerExceptionMsg(err, t) ?? t('task:TopTasks.UploadEvidenceFailed');
      appContext.showNotification(msg, true, false, true);
    } finally {
      setIsResolvingEvidence(false);
    }
  };

  const loadTemplates = async () => {
    try {
      if (templatesLoading) return;
      setTemplatesLoading(true);

      let _templateTasks = await appContext.globalDataCache.templates.getItems();
      _templateTasks = _templateTasks.filter((t) => !t.isCompleted() && t.taskKind === TaskKinds.None);

      if (!hasUserFeature(appContext, FeatureTypes.Events)) {
        _templateTasks = _templateTasks.filter((t) => t.taskType !== TaskTypes.EventTemplate);
      }

      _templateTasks.sort((a, b) => sortOnString(a.name, b.name));
      setTemplates(_templateTasks);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setTemplatesLoading(false);
    }
  };

  const getForms = (): IContextualMenuItem[] => {
    //a hidden form is a generated one (an audit procedure's own) and is offered nowhere. It is in
    //this cache all the same: the login fills it with the query filters off, so the filter that
    //keeps hidden forms out of GET tasks/types does not reach it
    let types = appContext.globalDataCache.taskTypes.items.filter((t) => !t.isHidden);
    const forms: IContextualMenuItem[] = [];

    //When specified, show only forms of the specified system type
    //In this case, hide functions to remove/add forms
    if (props.systemTaskType !== undefined) {
      types = types.filter((t) => t.systemTaskType === props.systemTaskType);
    } else {
      //When not specified, hide all system forms
      types = types.filter((t) => !t.systemTaskType);
    }

    // The commands sit in one submenu, above the list: with a tenant's worth of forms the menu is a
    // scrolling list, and a command underneath it could only be reached by scrolling to the bottom.
    // The submenu item is pinned, so the filter box never removes it. The submenu is for managers
    // only: every command in it manages forms, which is manager work.
    if (props.systemTaskType === undefined && isManager) {
      const commands: IContextualMenuItem[] = [];

      commands.push({
        key: 'addnew',
        disabled: !hasUserRolePermission(appContext, PermissionTypes.CreateForm),
        iconProps: newIcon,
        text: t('task:TopTasks.AddNewForm'),
        onClick: () => {
          setShowCreateNewFormDialog(true);
        },
      });

      if (props.task.taskTypeId) {
        commands.push({
          key: 'remove',
          iconProps: deleteIcon,
          text: t('task:TopTasks.RemoveForm'),
          onClick: () => {
            if (props.onClickForm) props.onClickForm(0);
          },
        });
      }

      // Moves the open, unanswered instances of this recurring series to the form's current version.
      // The dialog acts on the saved state of the series master: a form picked in this modal but not
      // saved yet is not what the instances follow, so there is no dirty check here.
      const upgradeFamilyId = props.task.taskTypeId
        ? (props.task.taskTypeFamilyId ??
          appContext.globalDataCache.taskTypes.get(props.task.taskTypeId).taskTypeFamilyId)
        : undefined;
      if (
        upgradeFamilyId &&
        props.task.taskId !== -1 &&
        props.task.isSeries() &&
        (props.task.taskType === TaskTypes.Normal || props.task.taskType === TaskTypes.Monitoring)
      ) {
        commands.push({
          key: 'update',
          iconProps: { iconName: 'Sync' },
          text: t('task:TopTasks.UpdateFormVersion'),
          disabled: props.isActionPending,
          onClick: () => setUpgradeTarget({ taskTypeFamilyId: upgradeFamilyId, taskMasterId: props.task.taskId }),
        });
      }

      //Convert checklist to form
      if (
        !props.task.taskTypeId &&
        props.onConvertChecklist &&
        canUpdateTaskField(props.orgTask, props.canUpdate, TaskFieldTypes.ChecklistEdit, appContext)
      ) {
        commands.push({
          className: 'isoplanner-task-topbar-more-convertchecklist',
          key: 'convertchecklist',
          text: t('task:TopTasks.ConvertChecklist'),
          iconProps: { iconName: 'ChangeEntitlements' },
          disabled: props.task.checkList.items.length === 0 || !props.canSave || props.isActionPending,
          onClick: () => {
            if (props.onConvertChecklist) props.onConvertChecklist();
          },
        });
      }

      //the forms admin page, below the task-level commands. Not offered when the host navigates
      //externally (the task runs outside the main app, where the admin route does not exist)
      if (!props.navigateExternal) {
        commands.push({
          key: 'div2',
          itemType: IPContextualMenuItemType.Divider,
        });
        commands.push({
          key: 'manageall',
          iconProps: editIcon,
          text: t('task:TopTasks.ManageAllForms'),
          onClick: () => history.push('/admin/content/forms'),
        });
      }

      forms.push({
        key: 'manage',
        iconProps: settingsIcon,
        text: t('task:TopTasks.ManageForm'),
        subMenuProps: { items: commands },
      });
      forms.push({
        key: 'div1',
        itemType: IPContextualMenuItemType.Divider,
      });
    }

    if (types.length > 0) {
      //the list holds the current head of every form; a task pinned to an older version of a form
      //has that form, so the check mark follows the family, not the version id
      const boundFamilyId =
        props.task.formVersion?.taskTypeFamilyId ??
        appContext.globalDataCache.taskTypes.get(props.task.taskTypeId)?.taskTypeFamilyId;
      //the cache hands them over in whatever order it loaded them: a list to pick a form from is read
      //by name, so it is sorted by name here (a copy - the cache is shared)
      forms.push(
        ...[...types]
          .sort((a, b) => sortOnString(a.name, b.name))
          .map((t) => {
            return {
              key: t.taskTypeId.toString(),
              iconProps: { iconName: 'EntitlementPolicy' },
              data: t.taskTypeId,
              canCheck: true,
              checked:
                t.taskTypeFamilyId !== undefined && boundFamilyId !== undefined
                  ? t.taskTypeFamilyId === boundFamilyId
                  : props.task.taskTypeId === t.taskTypeId,
              text: t.name,
              onClick: () => {
                if (props.onClickForm) props.onClickForm(t.taskTypeId);
              },
            };
          }),
      );
    } else {
      forms.push({
        key: 'no-items',
        onRenderContent: () => {
          if (templatesLoading) {
            return <IPSpinner />;
          } else {
            //a manager creates a form from the Manage form submenu above, so no link here
            return <IPText styles={{ root: { padding: '0 4px' } }}>{t('task:TopTasks.NoFormItems')}</IPText>;
          }
        },
      });
    }

    return forms;
  };

  const checkNewFormName = (name: string): string | undefined => {
    //check duplicate name
    const types = appContext.globalDataCache.taskTypes.items;
    if (types.some((t) => t.name.toLowerCase() === name.toLowerCase())) {
      return t('forms:Errors.NameError');
    }

    return undefined;
  };

  const createAndSelectNewForm = async (name: string | undefined) => {
    try {
      if (!name) return;

      const newTaskType = new TaskType();
      newTaskType.name = name;
      newTaskType.trans = [new TaskType_Translation()];
      newTaskType.transIdx = 0;

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const createdTaskType = await apiAddTaskType(newTaskType, accessToken);
      if (createdTaskType) {
        appContext.globalDataCache.taskTypes.add(createdTaskType);
        if (props.onClickForm) props.onClickForm(createdTaskType.taskTypeId);
      }
    } catch (err) {
      appContext.setError(err);
    } finally {
      setShowCreateNewFormDialog(false);
      setNewTaskTypeName(undefined);
    }
  };

  const getMoreMenuOptions = (): IContextualMenuProps => {
    const menu: IContextualMenuProps = { items: [] };

    const cannotSaveAsNewTask =
      !isManager ||
      !hasUserRolePermission(appContext, PermissionTypes.CreateTask) ||
      props.task.taskId === -1 || //new tasks cannot be saved as new tasks
      props.task.isInstance() || //because instances could follow a recurrence pattern
      props.task.taskType === TaskTypes.Monitoring || //because instances must be created immediately and this function does not do that
      props.task.taskType === TaskTypes.Template || //templates cannot be copied at this time. This could be investigated further later
      props.task.taskType === TaskTypes.EventTemplate; //templates cannot be copied at this time. This could be investigated further later

    if (props.onSaveAsNewTask && !cannotSaveAsNewTask) {
      menu.items.push({
        className: 'isoplanner-task-topbar-more-saveasnew',
        key: 'EditAsNewTask',
        text: t('task:EditAsNewTask'),
        iconProps: newIcon,
        disabled: props.isActionPending,
        onClick: (
          ev?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
          item?: IContextualMenuItem,
        ) => {
          if (props.onSaveAsNewTask) props.onSaveAsNewTask(props.task.taskId);
        },
      });
    }

    if (props.onCreateNewTaskTemplate && props.task.taskId !== -1) {
      menu.items.push({
        className: 'isoplanner-task-topbar-more-saveastemplate',
        key: 'saveAsTemplate',
        text: t('task:Template.Save'),
        iconProps: templateIcon,
        disabled: !isManager || props.isActionPending,
        onClick: (
          ev?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
          item?: IContextualMenuItem,
        ) => {
          if (props.onCreateNewTaskTemplate) props.onCreateNewTaskTemplate(props.task.taskId);
        },
      });
    }

    if (props.onRunTemplateWorkflows && props.task.taskId !== -1) {
      menu.items.push({
        className: 'isoplanner-task-topbar-more-runtemplateworkflows',
        key: 'runTemplateWorkflows',
        text: t('task:TopTasks.RunTemplateWorkflows'),
        iconProps: { iconName: 'Flow' },
        disabled: !isManager || props.isActionPending,
        onClick: (
          ev?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
          item?: IContextualMenuItem,
        ) => {
          if (props.onRunTemplateWorkflows) props.onRunTemplateWorkflows(props.task);
        },
      });
    }

    //convert a saved normal task to an event or an annual plan task, or an event without subtasks back to a normal task
    if (props.onConvertTaskType && isManager && props.canUpdate && props.task.taskId !== -1) {
      //the server refuses system tasks (approval), audit work and, for an annual plan task, a missing CreateTask permission
      const canConvertNormalTask =
        props.task.taskType === TaskTypes.Normal &&
        props.task.isNonRecurring() &&
        props.task.systemTaskType === SystemTaskTypes.None &&
        !props.task.isAuditExecutionTask();
      const canConvertToEvent = canConvertNormalTask;
      const canConvertToAnnualPlan = canConvertNormalTask && hasUserRolePermission(appContext, PermissionTypes.CreateTask);
      const canConvertToTask =
        props.task.taskType === TaskTypes.Event && props.task.isSeries() && (props.task.instances?.length ?? 0) === 0;

      if (canConvertToEvent) {
        menu.items.push({
          className: 'isoplanner-task-topbar-more-converttoevent',
          key: 'convertToEvent',
          text: t('task:TopTasks.ConvertToEvent'),
          iconProps: eventIcon,
          disabled: !props.canSave || props.isActionPending,
          onClick: () => {
            if (props.onConvertTaskType) props.onConvertTaskType(TaskTypes.Event);
          },
        });
      }

      if (canConvertToAnnualPlan) {
        menu.items.push({
          className: 'isoplanner-task-topbar-more-converttoannualplan',
          key: 'convertToAnnualPlan',
          text: t('task:TopTasks.ConvertToAnnualPlan'),
          iconProps: { iconName: 'RepeatAll' },
          disabled: !props.canSave || props.isActionPending,
          onClick: () => {
            if (props.onConvertTaskType) props.onConvertTaskType(TaskTypes.Monitoring);
          },
        });
      }

      if (canConvertToTask) {
        menu.items.push({
          className: 'isoplanner-task-topbar-more-converttotask',
          key: 'convertToTask',
          text: t('task:TopTasks.ConvertToTask'),
          iconProps: taskIcon,
          disabled: !props.canSave || props.isActionPending,
          onClick: () => {
            if (props.onConvertTaskType) props.onConvertTaskType(TaskTypes.Normal);
          },
        });
      }
    }

    //shown disabled on an unsaved template: the settings are a row of the template, which needs its id
    if (props.onEvidenceLocation && isManager) {
      menu.items.push({
        className: 'isoplanner-task-topbar-more-evidencelocation',
        key: evidenceLocationKey,
        text: t('task:Template.EvidenceLocation'),
        iconProps: { iconName: 'FabricFolder' },
        disabled: props.task.taskId === -1 || props.isActionPending,
        onClick: () => {
          if (props.onEvidenceLocation) props.onEvidenceLocation();
        },
      });
    }

    //instances can only be removed for monitoring types
    //- subtasks of events & event templates can only be removed from the subtask list of the event
    //- instances of normal recurring tasks can never be removed (only by changing the recurrence pattern)
    if (
      props.onClickRemove &&
      props.task.taskId !== -1 &&
      (!props.task.isInstance() || props.task.taskType === TaskTypes.Monitoring)
    ) {
      if (menu.items.length > 0) {
        menu.items.push({
          key: 'div1',
          text: '-',
          itemType: IPContextualMenuItemType.Divider,
        });
      }
      menu.items.push({
        className: 'isoplanner-task-topbar-more-remove',
        key: 'remove',
        text: t('translation:General.Button.Remove'),
        iconProps: deleteIcon,
        disabled: !props.canDelete || props.isActionPending,
        onClick: () => {
          if (props.onClickRemove) props.onClickRemove(props.task.taskId);
        },
      });
    }

    return menu;
  };

  const getMoreOptions = (): JSX.Element | null => {
    //the evidence location item keeps the button on an unsaved template, where it is shown disabled
    const menu = getMoreMenuOptions();
    if (menu.items.length > 0 && menu.items.some((i) => i.disabled === false || i.key === evidenceLocationKey)) {
      return (
        <IPDefaultButton
          className="isoplanner-task-topbar-more"
          styles={moreButtonStyles}
          iconProps={moreIcon}
          menuProps={menu}
          onRenderMenuIcon={() => null}
        />
      );
    } else {
      return null;
    }
  };

  const getAssistantButton = (): JSX.Element | null => {
    //the assistant is a chat with the task, offered whenever the Copilot panel is available;
    //the criterion path opens the pane without the button
    if (
      !props.onOpenAssistant ||
      props.task.taskId === -1 ||
      appContext.isMobileView ||
      !isCopilotPanelAvailable(appContext)
    ) {
      return null;
    }

    const isOpen = props.assistantAction !== undefined;

    return (
      <IPStack.Item>
        <IPDefaultButton
          className="isoplanner-task-topbar-ai"
          styles={isOpen ? assistantOpenButtonStyles : topTaskButtonStyles}
          title={getAIAssistantName(appContext, t)}
          //like the navigation bar: a click opens the assistant, a click while it is open closes it; the pane
          //offers the task's actions itself
          onClick={() =>
            props.onOpenAssistant?.(
              isOpen
                ? undefined
                : props.task.taskKind === TaskKinds.Assessment && isAuditCriteriaPreviewEnabled(appContext)
                ? 'assessment'
                : 'explain',
            )
          }
          onRenderText={() => (
            <IPStack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
              <AIIcon size={16} />
              <IPText styles={{ root: { fontWeight: globalFontBoldWeight } }}>AI</IPText>
            </IPStack>
          )}
        />
      </IPStack.Item>
    );
  };

  //
  // Main render
  //
  const isNotMobile = !appContext.isMobileView;

  return (
    <IPStack horizontal wrap tokens={globalStackTokensGapSmall}>
      {props.onUploadEvidence &&
        hasEvidenceCategory && //only show when the task has an evidence category to upload to
        //a form with a document attachment field or a KPI that takes attachments is where evidence goes;
        //without one, the form offers no upload at all
        (!props.task.taskTypeId || props.formOffersDocumentUpload === false) &&
        props.task.isWorkableTask() && ( //only show for events and normal tasks (no templates and no series)
          <IPStack.Item>
            <IPDefaultButton
              className="isoplanner-task-topbar-upload"
              styles={topTaskButtonStyles}
              iconProps={uploadIcon}
              text={t('task:TopTasks.UploadEvidence')}
              disabled={isResolvingEvidence}
              onClick={() => uploadEvidence()}
            />
          </IPStack.Item>
        )}
      {isNotMobile && props.onClickTemplate && props.task.taskId === -1 && (
        <IPStack.Item>
          <IPDefaultButton
            className="isoplanner-task-topbar-template"
            styles={topTaskButtonStyles}
            iconProps={templateIcon}
            text={t('task:TopTasks.Template')}
            onMenuClick={() => loadTemplates()}
            menuProps={getTemplates}
          />
        </IPStack.Item>
      )}
      {isNotMobile && isManager && props.onClickOpenMaster && props.task.isInstance() && (
        <IPStack.Item>
          <IPTooltipHost content={t('task:Master.OpenLink')}>
            <IPDefaultButton
              className="isoplanner-task-topbar-openmaster"
              styles={topTaskButtonStyles}
              iconProps={{ iconName: 'Info' }}
              text={t('task:TopTasks.OpenMaster')}
              onClick={() => {
                if (props.onClickOpenMaster) props.onClickOpenMaster(props.task.taskMasterId);
              }}
            />
          </IPTooltipHost>
        </IPStack.Item>
      )}
      {isNotMobile &&
        isManager &&
        props.onClickOpenEvent &&
        props.task.isInstance() &&
        props.task.taskType === TaskTypes.Event && (
          <IPStack.Item>
            <IPTooltipHost content={t('task:TopTasks.OpenEventInfo')}>
              <IPDefaultButton
                className="isoplanner-task-topbar-openevent"
                styles={topTaskButtonStyles}
                iconProps={{ iconName: 'Info' }}
                text={t('task:TopTasks.OpenEvent')}
                onClick={() => {
                  if (props.onClickOpenEvent) props.onClickOpenEvent(props.task.taskMasterId);
                }}
              />
            </IPTooltipHost>
          </IPStack.Item>
        )}
      {isNotMobile &&
        canUpdateTaskField(props.orgTask, props.canUpdate, TaskFieldTypes.Form, appContext) &&
        props.onClickForm &&
        hasUserFeature(appContext, FeatureTypes.KPIsAndForms) && (
          <IPStack.Item>
            <IPDefaultButton
              className="isoplanner-task-topbar-form"
              styles={topTaskButtonStyles}
              iconProps={formIcon}
              text={t('task:TopTasks.Form')}
              //bounded like the templates menu: a tenant with dozens of forms otherwise opens a menu
              //that runs off the screen instead of scrolling inside itself
              menuProps={{
                items: getForms(),
                ...getSearchableMenuProps({ pinnedKeys: ['manage'] }),
              }}
            />
            {upgradeTarget && (
              <FormUpgradeModal
                taskTypeFamilyId={upgradeTarget.taskTypeFamilyId}
                taskMasterId={upgradeTarget.taskMasterId}
                isOpen={true}
                onDismiss={() => setUpgradeTarget(undefined)}
                onUpgraded={(count) => {
                  if (count > 0) void props.onInstancesFormUpgraded?.();
                }}
              />
            )}
            {showCreateNewFormDialog && (
              <ModifyTextDialog
                isOpen={showCreateNewFormDialog}
                value={newTaskTypeName}
                required={true}
                title={t('forms:Fields.Name')}
                placeholder={t('forms:Fields.NamePlaceholder')}
                maxLength={512}
                onClose={() => {
                  setShowCreateNewFormDialog(false);
                  setNewTaskTypeName(undefined);
                }}
                onSave={(newName) => createAndSelectNewForm(newName)}
                onUpdate={(newName: string) => setNewTaskTypeName(newName)}
                onGetErrorMessage={(newName) => checkNewFormName(newName)}
              />
            )}
          </IPStack.Item>
        )}
      {isNotMobile &&
        props.onClickFollowUp &&
        props.task.taskId !== -1 &&
        hasUserRolePermission(appContext, PermissionTypes.CreateTask) &&
        renderFollowUpButton()}
      {props.onClickFinish &&
        props.task.taskId !== -1 &&
        props.orgTask.taskStateId !== TaskStateId.Completed &&
        props.orgTask.taskStateId !== TaskStateId.Archived &&
        (!props.task.isSeries() || props.task.taskType === TaskTypes.Event) &&
        canUpdateTaskField(props.orgTask, props.canUpdate, TaskFieldTypes.Status, appContext) && (
          <IPStack.Item>
            <IPDefaultButton
              className="isoplanner-task-topbar-finish"
              styles={topTaskButtonStyles}
              disabled={!props.canSave || props.isActionPending}
              iconProps={{ iconName: 'FavoriteStarFill' }}
              text={t('task:TopTasks.Finish')}
              onClick={props.onClickFinish}
            />
          </IPStack.Item>
        )}
      {props.onClickArchive &&
        !props.isLoading &&
        props.task.taskId !== -1 &&
        (props.orgTask.taskStateId === TaskStateId.Completed || props.orgTask.taskStateId === TaskStateId.Rejected) &&
        (!props.task.isSeries() || props.task.taskType === TaskTypes.Event) &&
        canUpdateTaskField(props.orgTask, props.canUpdate, TaskFieldTypes.Status, appContext) && (
          <IPStack.Item>
            <IPDefaultButton
              className="isoplanner-task-topbar-archive"
              styles={topTaskButtonStyles}
              disabled={!props.canSave || props.isActionPending}
              iconProps={{ iconName: 'FavoriteStarFill' }}
              text={t('task:TopTasks.Archive')}
              onClick={props.onClickArchive}
            />
          </IPStack.Item>
        )}
      {props.onClickReopen &&
        !props.isLoading &&
        props.task.taskId !== -1 &&
        (props.orgTask.taskStateId === TaskStateId.Completed || props.orgTask.taskStateId === TaskStateId.Archived) &&
        (!props.task.isSeries() || props.task.taskType === TaskTypes.Event) &&
        canUpdateTaskField(props.orgTask, props.canUpdate, TaskFieldTypes.Status, appContext) && (
          <IPStack.Item>
            <IPDefaultButton
              className="isoplanner-task-topbar-reopen"
              styles={topTaskButtonStyles}
              disabled={!props.canSave || props.isActionPending}
              iconProps={{ iconName: 'Undo' }}
              text={t('task:TopTasks.Reopen')}
              onClick={props.onClickReopen}
            />
          </IPStack.Item>
        )}
      {getAssistantButton()}
      {getMoreOptions()}
    </IPStack>
  );
};

export default TaskTopTasksBar;
